import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/ui/avatar";
import { Badge } from "@shared/ui/badge";
import { Card } from "@shared/ui/card";
import {
  Calendar,
  Mail,
  MapPin,
  Phone,
  Shield,
  Smartphone,
  User,
  Briefcase,
  Key,
} from "lucide-react";
import MaskedSSN from "@features/cx/shared/components/MaskedSSN";
import { useParams } from "react-router-dom";
import { useGetFieldForceDetails } from "../../hooks";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";

export const InfoCard = () => {
  const { ff_no } = useParams<{ ff_no: string }>();
  const { data: technician } = useGetFieldForceDetails(ff_no || "");

  const pending = (technician?.total || 0) - (technician?.completed || 0);

  const infoItems = [
    {
      label: "Email",
      value: <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="font-medium text-slate-900 flex items-center gap-1 truncate">
                      <Mail size={14} className="text-blue-500" />
                      {technician?.email || "N/A"}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{technician?.email || "N/A"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>,
      icon: <Mail className="h-3.5 w-3.5 text-gray-400" />,
    },
    {
      label: "Phone",
      value: technician?.contactNumber || "N/A",
      icon: <Phone className="h-3.5 w-3.5 text-gray-400" />,
    },
    {
      label: "Location",
      value: (
        <div className="max-w-[200px]">
          {technician?.addressMap?.addressLine || 
           [technician?.areaName, technician?.subAreaName].filter(Boolean).flat().join(", ") || 
           "N/A"}
        </div>
      ),
      icon: <MapPin className="h-3.5 w-3.5 text-gray-400" />,
    },
    {
      label: "Department",
      value: technician?.extraDataMap?.department?.join(", ") || "NA", 
      icon: <Briefcase className="h-3.5 w-3.5 text-gray-400" />,
    },
    {
      label: "App Version",
      value: technician?.version || "NA",
      icon: <Smartphone className="h-3.5 w-3.5 text-gray-400" />,
    },
    {
        label: "Password",
        value: <div className="text-sm font-medium"><MaskedSSN ssn={technician?.password} /></div>,
        icon: <Key className="h-3.5 w-3.5 text-gray-400" />,
    },
    {
      label: "Created By",
      value: technician?.createdUserRemoteName || "NA",
      icon: <User className="h-3.5 w-3.5 text-gray-400" />,
    },
    {
      label: "Created On",
      value: technician?.createdDate || "N/A",
      icon: <Calendar className="h-3.5 w-3.5 text-gray-400" />,
    },
  ];

  return (
    <Card className="p-6">
      <div className="flex justify-between items-start mb-8">
        <div className="flex gap-4">
          <Avatar className="h-16 w-16 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">
            <AvatarImage src={technician?.file} />
            <AvatarFallback className="rounded-xl bg-blue-50 text-blue-600 text-xl font-medium">
              {technician?.name?.charAt(0) || <User className="h-8 w-8" />}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-bold text-gray-900">{technician?.name || "Loading..."}</h2>
              <Badge
                variant="outline"
                 className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      technician?.isActive
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
              >
                 {technician?.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-gray-500">{technician?.roleName || "Field Force"}</p>
          </div>
        </div>
        
        <div className="flex gap-8 text-center">
            <div>
                <p className="text-xs text-gray-500 font-medium mb-1">Assigned</p>
                 <p className="text-2xl font-bold text-blue-600">{technician?.total || 0}</p>
            </div>
            <div>
                <p className="text-xs text-gray-500 font-medium mb-1">Pending</p>
                 <p className="text-2xl font-bold text-orange-500">{technician?.pending}</p>
            </div>
            <div>
                <p className="text-xs text-gray-500 font-medium mb-1">Completed</p>
                 <p className="text-2xl font-bold text-green-600">{technician?.completed || 0}</p>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-4">
        {infoItems.map((item, index) => (
          <ValueLabel
            key={index}
            label={item.label}
            value={item.value}
          />
        ))}
      </div>
    </Card>
  );
};
